<template>
  <div class="single-metric-chart">
    <div class="chart-header">
      <h4 class="chart-title">{{ title }}</h4>
      <p class="chart-subtitle">{{ subtitle }}</p>
    </div>
    <div class="chart-container">
      <Line :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Chart.js 등록
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  data: {
    type: Array,
    required: true
  },
  metricKey: {
    type: String,
    required: true
  },
  color: {
    type: String,
    default: '#22d3ee'
  },
  showThreeYearAverage: {
    type: Boolean,
    default: false
  },
  showYoYLayer: {
    type: Boolean,
    default: false
  }
});

// 숫자 문자열을 숫자로 변환 (쉼표 제거)
const parseNumber = (str) => {
  if (!str || str === '-') return 0;
  return parseInt(str.replace(/,/g, ''));
};

// 억 단위로 변환
const toHundredMillion = (value) => {
  return Math.round(value / 100000000);
};

// 데이터 정렬 (날짜순)
const sortedData = computed(() => {
  return [...props.data].sort((a, b) => {
    return a.business_year.localeCompare(b.business_year);
  });
});

// 연도 추출 (YYYY.MM-YYYY.MM 형식에서 첫 번째 연도)
const extractYear = (businessYear) => {
  if (!businessYear) return null;
  const match = businessYear.match(/^(\d{4})\./);
  return match ? parseInt(match[1]) : null;
};

// 분기 추출 (YYYY.MM-YYYY.MM 형식에서 분기 번호)
const extractQuarter = (businessYear) => {
  if (!businessYear) return null;
  const match = businessYear.match(/\.(\d{2})-/);
  if (match) {
    const month = parseInt(match[1]);
    if (month >= 1 && month <= 3) return 1;
    if (month >= 4 && month <= 6) return 2;
    if (month >= 7 && month <= 9) return 3;
    if (month >= 10 && month <= 12) return 4;
  }
  return null;
};

// 전년 동기 데이터 찾기
const findPreviousYearData = (currentItem, allData) => {
  if (!currentItem || !currentItem.business_year) return null;
  
  const currentYear = extractYear(currentItem.business_year);
  const currentQuarter = extractQuarter(currentItem.business_year);
  if (!currentYear || !currentQuarter) return null;

  const prevYear = currentYear - 1;
  
  // 분기별 시작/종료 월 계산
  let startMonth, endMonth;
  if (currentQuarter === 1) {
    startMonth = '01';
    endMonth = '03';
  } else if (currentQuarter === 2) {
    startMonth = '04';
    endMonth = '06';
  } else if (currentQuarter === 3) {
    startMonth = '07';
    endMonth = '09';
  } else if (currentQuarter === 4) {
    startMonth = '10';
    endMonth = '12';
  } else {
    return null;
  }
  
  const prevYearStr = `${prevYear}.${startMonth}-${prevYear}.${endMonth}`;
  
  return allData.find(item => item.business_year === prevYearStr);
};

// 3년 평균 계산
const calculateThreeYearAverage = computed(() => {
  if (!props.showThreeYearAverage || sortedData.value.length === 0) return null;
  
  // 최근 3년(12분기) 데이터 사용
  const recentData = sortedData.value.slice(-12);
  if (recentData.length === 0) return null;
  
  const values = recentData
    .map(item => parseNumber(item.financial[props.metricKey]))
    .filter(val => val > 0);
  
  if (values.length === 0) return null;
  
  const sum = values.reduce((acc, val) => acc + val, 0);
  const average = sum / values.length;
  return toHundredMillion(average);
});

// 차트 데이터
const chartData = computed(() => {
  const labels = sortedData.value.map(item => item.business_year);
  
  const values = sortedData.value.map(item => 
    toHundredMillion(parseNumber(item.financial[props.metricKey]))
  );

  const datasets = [
    {
      label: props.title,
      data: values,
      borderColor: props.color,
      backgroundColor: `${props.color}20`,
      tension: 0.4,
      fill: true,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBackgroundColor: props.color,
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    }
  ];

  // 3년 평균선 추가
  if (props.showThreeYearAverage && calculateThreeYearAverage.value !== null) {
    const averageValue = calculateThreeYearAverage.value;
    datasets.push({
      label: '3년 평균',
      data: labels.map(() => averageValue),
      borderColor: '#fbbf24',
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [5, 5],
      tension: 0,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0
    });
  }

  // 전년 동기 대비 레이어 추가 (전년 동기 값 자체를 표시)
  if (props.showYoYLayer) {
    const yoyValues = sortedData.value.map(item => {
      const prevYearData = findPreviousYearData(item, sortedData.value);
      if (prevYearData && prevYearData.financial && prevYearData.financial[props.metricKey]) {
        const prevValue = parseNumber(prevYearData.financial[props.metricKey]);
        if (prevValue !== 0) {
          return toHundredMillion(prevValue);
        }
      }
      return null;
    });

    datasets.push({
      label: '전년 동기',
      data: yoyValues,
      borderColor: '#f97316',
      backgroundColor: 'transparent',
      borderWidth: 3,
      tension: 0.4,
      fill: false,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBackgroundColor: '#f97316',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      borderDash: [3, 3],
      spanGaps: true
    });
  }

  return {
    labels,
    datasets
  };
});

// 차트 옵션
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: props.showThreeYearAverage || props.showYoYLayer,
      position: 'top',
      labels: {
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        },
        usePointStyle: true,
        padding: 15
      }
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      titleColor: '#fff',
      bodyColor: '#e5e7eb',
      borderColor: '#374151',
      borderWidth: 1,
      padding: 12,
      displayColors: true,
      callbacks: {
        label: function(context) {
          if (context.parsed.y === null) return null;
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          label += context.parsed.y.toLocaleString() + '억원';
          return label;
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        color: 'rgba(255, 255, 255, 0.05)',
        drawBorder: false
      },
      ticks: {
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(255, 255, 255, 0.05)',
        drawBorder: false
      },
      ticks: {
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        },
        callback: function(value) {
          return value.toLocaleString() + '억';
        }
      },
      title: {
        display: true,
        text: '(억원)',
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        }
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  }
}));
</script>

<style scoped>
.single-metric-chart {
  background: #1f2937;
  border-radius: 12px;
  padding: 20px;
  height: 100%;
}

.chart-header {
  margin-bottom: 16px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #f9fafb;
  margin: 0 0 4px 0;
}

.chart-subtitle {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

.chart-container {
  height: 280px;
  position: relative;
}
</style>





